import { DateRangePreset, DateRangeValue } from '../components/common/types';

export const DEFAULT_DATE_RANGE_PRESET_VALUE = 'last25Months';

function toISODate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function daysBefore(referenceDate: Date, days: number): Date {
  const result = new Date(referenceDate);
  result.setDate(result.getDate() - days);
  return result;
}

function monthsBefore(referenceDate: Date, months: number): Date {
  const result = new Date(referenceDate);
  result.setMonth(result.getMonth() - months);
  return result;
}

function calendarYearRange(year: number): Pick<DateRangePreset, 'startDate' | 'endDate'> {
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

/**
 * AllOrdersTable's built-in "Date Placed" preset menu — matches the real production Purchase
 * History experience's own preset set (relative windows plus full calendar years, not an evenly
 * spaced relative ladder). Computed relative to `referenceDate` (defaults to `new Date()`) rather
 * than hardcoded literal dates, so it never goes stale — pass a fixed `referenceDate` in tests for
 * deterministic assertions.
 */
export function buildDateRangePresets(referenceDate: Date = new Date()): DateRangePreset[] {
  const today = toISODate(referenceDate);
  const currentYear = referenceDate.getFullYear();

  return [
    { value: 'last30Days', label: 'Last 30 Days', startDate: toISODate(daysBefore(referenceDate, 30)), endDate: today },
    { value: 'last60Days', label: 'Last 60 Days', startDate: toISODate(daysBefore(referenceDate, 60)), endDate: today },
    {
      value: DEFAULT_DATE_RANGE_PRESET_VALUE,
      label: 'Last 25 Months',
      startDate: toISODate(monthsBefore(referenceDate, 25)),
      endDate: today
    },
    { value: String(currentYear), label: String(currentYear), ...calendarYearRange(currentYear) },
    { value: String(currentYear - 1), label: String(currentYear - 1), ...calendarYearRange(currentYear - 1) },
    { value: String(currentYear - 2), label: String(currentYear - 2), ...calendarYearRange(currentYear - 2) }
  ];
}

/** AllOrdersTable's built-in default selection — "Last 25 Months", matching the real production
 * Purchase History experience's own default. */
export function buildDefaultDateRange(referenceDate: Date = new Date()): DateRangeValue {
  const preset = buildDateRangePresets(referenceDate).find(
    (candidate) => candidate.value === DEFAULT_DATE_RANGE_PRESET_VALUE
  );
  return preset ? { preset: preset.value, startDate: preset.startDate, endDate: preset.endDate } : {};
}

export const DEFAULT_ACTIVE_ORDERS_DATE_RANGE_PRESET_VALUE = 'all';

function daysAfter(referenceDate: Date, days: number): Date {
  return daysBefore(referenceDate, -days);
}

/**
 * ActiveOrdersTable's built-in "Pickup by" preset menu — All plus forward-looking (Today/Tomorrow/
 * Next 7 Days), unlike AllOrdersTable's backward-looking "Date Placed" presets above. Computed relative
 * to `referenceDate` (defaults to `new Date()`) rather than hardcoded literal dates — pass a
 * fixed `referenceDate` in tests for deterministic assertions. 'All' is unbounded (sent as the
 * 'ALL' sentinel, no date narrowing); its startDate/endDate are placeholders required by the type.
 */
export function buildActiveOrdersDateRangePresets(referenceDate: Date = new Date()): DateRangePreset[] {
  const today = toISODate(referenceDate);
  const tomorrow = toISODate(daysAfter(referenceDate, 1));
  const next7DaysEnd = toISODate(daysAfter(referenceDate, 7));

  return [
    { value: DEFAULT_ACTIVE_ORDERS_DATE_RANGE_PRESET_VALUE, label: 'All', startDate: today, endDate: today },
    { value: 'today', label: 'Today', startDate: today, endDate: today },
    { value: 'tomorrow', label: 'Tomorrow', startDate: tomorrow, endDate: tomorrow },
    { value: 'next7Days', label: 'Next 7 Days', startDate: today, endDate: next7DaysEnd }
  ];
}

/** ActiveOrdersTable's built-in default selection — "All". */
export function buildDefaultActiveOrdersDateRange(referenceDate: Date = new Date()): DateRangeValue {
  const preset = buildActiveOrdersDateRangePresets(referenceDate).find(
    (candidate) => candidate.value === DEFAULT_ACTIVE_ORDERS_DATE_RANGE_PRESET_VALUE
  );
  return preset ? { preset: preset.value, startDate: preset.startDate, endDate: preset.endDate } : {};
}
